import type { PuzzleContent } from '@roots/schema';
// Edition reader — App Flow §4: scroll-snap pages, optimistic hearts, drop reveal, offline cache.
import { Button, Card, CaretLeft, HeartButton, LoadingState, cx, useToast } from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  CoverPage,
  CreditsPage,
  FoodPage,
  HoroscopePage,
  OccasionsPage,
  PhotoCollectionPage,
  PlaceholderPage,
  PuzzlePage,
  StoryPage,
  TravelPage,
} from '../components/edition-pages.js';
import { api, withQueue } from '../lib/api.js';
import { cacheDoc, readCached } from '../lib/offline.js';

type EditionDetail = Awaited<ReturnType<typeof api.edition>>;

export function EditionReaderScreen() {
  const { editionId } = useParams({ strict: false }) as { editionId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const trackRef = useRef<HTMLDivElement>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['edition', editionId],
    queryFn: async () => {
      try {
        const fresh = await api.edition(editionId);
        await cacheDoc(`edition:${editionId}`, fresh);
        return fresh;
      } catch (err) {
        const cached = await readCached<EditionDetail>(`edition:${editionId}`);
        if (cached) return cached;
        throw err;
      }
    },
  });

  useEffect(() => {
    if (!data) return;
    void api.markEditionOpened(editionId).catch(() => undefined); // PRD §7 metric
    const seenKey = `roots:seen:${editionId}`;
    if (!localStorage.getItem(seenKey)) {
      setRevealed(true); // UI/UX §6 — the one 600ms reveal
      localStorage.setItem(seenKey, '1');
    }
  }, [data, editionId]);

  const react = useMutation({
    mutationFn: async ({ targetId, existingId }: { targetId: string; existingId?: string }) => {
      if (existingId) {
        return withQueue(
          { kind: 'reaction.remove', payload: { editionId, reactionId: existingId } },
          () => api.unreact(editionId, existingId),
        );
      }
      return withQueue(
        {
          kind: 'reaction.add',
          payload: { editionId, target_type: 'page', target_id: targetId },
        },
        () => api.react(editionId, { target_type: 'page', target_id: targetId, type: 'heart' }),
      );
    },
    // App Flow §7.2 — optimistic, with rollback.
    onMutate: async ({ targetId, existingId }) => {
      await queryClient.cancelQueries({ queryKey: ['edition', editionId] });
      const previous = queryClient.getQueryData<EditionDetail>(['edition', editionId]);
      queryClient.setQueryData<EditionDetail>(['edition', editionId], (current) => {
        if (!current) return current;
        const key = `page:${targetId}`;
        const counts = { ...current.reaction_counts };
        counts[key] = Math.max(0, (counts[key] ?? 0) + (existingId ? -1 : 1));
        return {
          ...current,
          reaction_counts: counts,
          my_reactions: existingId
            ? current.my_reactions.filter((r) => r.id !== existingId)
            : [
                ...current.my_reactions,
                { id: `optimistic-${targetId}`, target_type: 'page' as const, target_id: targetId },
              ],
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['edition', editionId], context.previous);
      push('That didn’t save. Try again in a moment.', 'error');
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['edition', editionId] }),
  });

  if (isLoading) {
    return (
      <LoadingState onRetry={() => void refetch()}>
        <div className="roots-skeleton h-[60vh] w-full" />
      </LoadingState>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <p className="type-body">Ask a family admin to unlock earlier editions for you.</p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/newsletter' })}>
          Back to editions
        </Button>
      </div>
    );
  }

  const myHeart = (index: number) =>
    data.my_reactions.find((r) => r.target_type === 'page' && r.target_id === String(index));
  const heartCount = (index: number) => data.reaction_counts[`page:${index}`] ?? 0;
  const toggleHeart = (index: number) =>
    react.mutate({ targetId: String(index), existingId: myHeart(index)?.id });

  return (
    <div className="flex gap-6">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="md:invisible"
            onClick={() => void navigate({ to: '/newsletter' })}
          >
            <CaretLeft size={18} aria-hidden /> Editions
          </Button>
          <p className="type-caption">
            Page {pageIndex + 1} of {data.pages.length}
          </p>
        </div>

        <div
          ref={trackRef}
          className={cx(
            'reader-track roots-paper rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] shadow-[var(--shadow-card)]',
            revealed && 'roots-reveal', // UI/UX §6 — the one 600ms drop reveal
          )}
          onScroll={(e) => {
            const el = e.currentTarget;
            setPageIndex(Math.round(el.scrollLeft / el.clientWidth));
          }}
        >
          {data.pages.map((page, index) => (
            <section
              key={`${page.type}-${index}`}
              className="reader-page relative min-h-[70vh]"
              aria-label={`Page ${index + 1}`}
            >
              <RenderPage page={page} />
              {/* §3.9 floating heart; on ≥1024 it moves to the reactions column (§5.3). */}
              <div className="absolute bottom-4 right-4 lg:hidden">
                <HeartButton
                  hearted={Boolean(myHeart(index))}
                  count={heartCount(index)}
                  onToggle={() => toggleHeart(index)}
                />
              </div>
            </section>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-1">
          {data.pages.map((page, index) => (
            <button
              key={`dot-${page.type}-${index}`}
              type="button"
              aria-label={`Go to page ${index + 1}`}
              onClick={() => {
                const el = trackRef.current;
                if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
              }}
              className="flex h-11 w-6 items-center justify-center"
            >
              <span
                className={cx(
                  'h-2 w-2 rounded-full',
                  index === pageIndex
                    ? 'bg-[var(--color-accent-primary)]'
                    : 'bg-[var(--color-accent-warm-gray)]',
                )}
              />
            </button>
          ))}
        </div>
      </div>

      {/* §5.3 — page-level reactions column (reserved for future comments). */}
      <aside className="hidden w-[200px] shrink-0 lg:block">
        <Card className="sticky top-20 flex flex-col items-center gap-3 text-center">
          <p className="type-label text-[var(--color-text-secondary)]">Page {pageIndex + 1}</p>
          <HeartButton
            hearted={Boolean(myHeart(pageIndex))}
            count={heartCount(pageIndex)}
            onToggle={() => toggleHeart(pageIndex)}
          />
          <p className="type-caption">
            {heartCount(pageIndex) === 0
              ? 'Be the first to heart this page.'
              : `${heartCount(pageIndex)} ${heartCount(pageIndex) === 1 ? 'heart' : 'hearts'} on this page`}
          </p>
        </Card>
      </aside>
    </div>
  );
}

function RenderPage({ page }: { page: EditionDetail['pages'][number] }) {
  switch (page.type) {
    case 'cover':
      return (
        <CoverPage
          theme={page.theme}
          monthLabel={page.month_label}
          familyName={page.family_name}
          editorName={page.editor_name}
          memberCount={page.member_count}
        />
      );
    case 'story':
      return (
        <StoryPage
          memberName={page.member_name}
          promptText={page.prompt_text}
          narrative={page.narrative}
          photos={page.photos as never}
          layoutHint={page.layout_hint}
        />
      );
    case 'placeholder':
      return <PlaceholderPage message={page.message} />;
    case 'credits':
      return <CreditsPage editorName={page.editor_name} contributors={page.contributors} />;
    case 'department':
      switch (page.department_type) {
        case 'puzzle':
          return <PuzzlePage content={page.content as PuzzleContent} />;
        case 'horoscope':
          return <HoroscopePage content={page.content as never} />;
        case 'food':
          return <FoodPage content={page.content as never} />;
        case 'travel':
          return <TravelPage content={page.content as never} />;
        case 'photo_collection':
          return <PhotoCollectionPage content={page.content as never} />;
        case 'occasions':
          return <OccasionsPage content={page.content as never} />;
        default:
          return null;
      }
    default:
      return null;
  }
}
